export type PendingRole = 'PROVIDER' | 'EMPLOYER';

/** The choice, plus WHEN it was made -- see `takePendingRole`. */
export type PendingRoleIntent = {
  role: PendingRole;
  chosenAt: number;
};

const KEY = 'pendingRegistrationRole';
/** Pre-MCV-1591 key, written to localStorage and never read. Cleared on sight. */
const LEGACY_KEY = 'role';

/**
 * [MCV-1591] The role a visitor chose BEFORE signing up, carried across Clerk.
 *
 * Two things bound it, because neither is sufficient alone.
 *
 * 1. sessionStorage, not localStorage: the value describes one signup attempt,
 *    not the browser. It still survives the navigation that matters -- the
 *    Clerk satellite hand-off leaves HLX for the primary and returns to HLX in
 *    the same tab, and sessionStorage is per-origin per-tab.
 *
 * 2. `chosenAt`: the consumer requires the signed-in account to have been
 *    CREATED AFTER the choice was made. An account that predates the choice
 *    cannot be the account the choice was for, so an abandoned intent can never
 *    be applied to a pre-existing one -- however recently that account happened
 *    to be created. Account age alone was only a proxy for this relationship
 *    and let a different account created minutes earlier through.
 */
export const setPendingRole = (role: PendingRole): void => {
  try {
    const intent: PendingRoleIntent = { role, chosenAt: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(intent));
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Storage can be unavailable (Safari private mode, blocked third-party
    // storage on the satellite). Losing the hint costs the user one extra
    // question on RoleSelectPage, which is the safe direction to fail.
  }
};

/** Null if absent, unparseable, or not one of the two roles. */
const parseIntent = (stored: string | null): PendingRoleIntent | null => {
  if (!stored) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }

  const { role, chosenAt } = (parsed ?? {}) as Partial<PendingRoleIntent>;
  if (role !== 'PROVIDER' && role !== 'EMPLOYER') return null;
  if (typeof chosenAt !== 'number' || !Number.isFinite(chosenAt)) return null;

  return { role, chosenAt };
};

/**
 * Reads and clears the pending intent. Returns null if absent, unparseable, or
 * not one of the two roles -- including a value left by an older release, whose
 * bare-string format no longer parses.
 */
export const takePendingRole = (): PendingRoleIntent | null => {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }

  // Separate try: legacy cleanup is best-effort and must not discard a value
  // we have already read and removed. Sharing one try/catch here meant a
  // localStorage failure returned null AFTER consuming the sessionStorage
  // entry -- the choice was destroyed and ignored in the same breath.
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Ignored; see setPendingRole.
  }

  return parseIntent(stored);
};

/**
 * [MCV-1762] The pending role WITHOUT consuming it. A Clerk sub-step of the sign-in or
 * sign-up card (`/sign-in/create`, `/sign-up/continue`) drops the `?role=` query, so the page
 * reads the choice back from here to know which flow it is still rendering. Only
 * `takePendingRole` may consume the intent: it is the one that checks `chosenAt`.
 */
export const peekPendingRole = (): PendingRole | null => {
  try {
    return parseIntent(sessionStorage.getItem(KEY))?.role ?? null;
  } catch {
    return null;
  }
};

/**
 * [MCV-1666] Maps the `role` query param `LoginOrCreateAccountPage` appends
 * to the sign-in link (`?role=provider` / `?role=employer`) to a
 * `PendingRole`. Returns null for anything else, including an absent or
 * malformed value -- an invalid role must never call `setPendingRole`.
 */
export const roleFromQueryValue = (value: string | null): PendingRole | null => {
  if (value === 'provider') return 'PROVIDER';
  if (value === 'employer') return 'EMPLOYER';
  return null;
};

export const clearPendingRole = (): void => {
  try {
    sessionStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // See setPendingRole.
  }
};
